import DroppableColumnWrapper from "@/components/DroppableColumnWrapper";
import OrderCard from "@/components/OrderCard";
import OrderColumn from "@/components/OrderColumn";
import { useUserRole } from "@/hooks/useUserRole";
import { Order } from "@/types/ApiTypes";
import { App } from '@capacitor/app';

import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    DragStartEvent,
    DragEndEvent,
    TouchSensor,
    rectIntersection,
} from "@dnd-kit/core";
import axios, { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

type ColumnId = "new" | "preparing" | "finished";

export default function ChefsOrdersBoard() {
    const {isChef,openAppReviewBasedOnRole} = useUserRole()

    const [newOrders, setNewOrders] = useState<Order[]>([]);
    const [preparingOrders, setPreparingOrders] = useState<Order[]>([]);
    const [finishedOrders, setFinishedOrders] = useState<Order[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const base_socket = import.meta.env.VITE_SOCKET;
    const manuallyClosed = useRef(false);
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // fine-tuned for desktop
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );
    const updateOrders = (incomingOrder: Order) => {
        setNewOrders((prev) => prev.filter((order) => order._id !== incomingOrder._id));
        setPreparingOrders((prev) => prev.filter((order) => order._id !== incomingOrder._id));
        setFinishedOrders([]); // Always clear finished orders

        if (
            incomingOrder.status === "cancelled" ||
            (incomingOrder.status === "returned" && incomingOrder.progress === null)
        ) {
            return; // Ignore cancelled orders
        }

        if (incomingOrder.progress === null) {
            setNewOrders((prev) => [incomingOrder, ...prev]);
        } else if (incomingOrder.progress === "preparing") {
            setPreparingOrders((prev) => [incomingOrder, ...prev]);
        }
        // ❌ Do not push into finishedOrders anymore
    };


    useEffect(() => {
        if (!token || !base_socket) return;

        let socket: WebSocket | null = null;
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
        let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

        manuallyClosed.current = false;

        const connectWebSocket = () => {
            if (socket && socket.readyState <= 1) return; // Already connecting/open

            toast.loading(t("chef.toast.connecting"), { id: "chef-ws" });

            socket = new WebSocket(`${base_socket}/socket/dashboard/orders`);

            socket.addEventListener("open", () => {
                toast.success(t("chef.toast.connected"), { id: "chef-ws" });
                socket?.send(JSON.stringify({ type: "authorize", token }));

                // Heartbeat every 15s
                heartbeatInterval = setInterval(() => {
                    if (socket?.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: "ping" }));
                    }
                }, 15000);
            });
            socket.addEventListener("message", (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const order = data.order;

                    if (!order) return;
                    updateOrders(order);
                    if (order.status === "cancelled") {
                        toast.error(
                            t("chef.toast.cancelled", { id: order.orderId }),
                            { id: `order-${order.orderId}` }
                        );
                    } else if (order.status === "returned") {

                        toast.error(
                            t("chef.toast.returned", { id: order.orderId }),
                            { id: `order-${order.orderId}` }
                        );
                    } else {

                        toast.success(
                            t("chef.toast.newUpdate", { id: order.orderId }),
                            { id: `order-${order.orderId}` }
                        );
                    }
                } catch {
                    // Silent fail
                }
            });


            socket.addEventListener("close", () => {
                clearInterval(heartbeatInterval!);
                heartbeatInterval = null;

                if (!manuallyClosed.current) {
                    toast.error(t("chef.toast.disconnected"), { id: "chef-ws" });
                    getOrders(); // Refresh orders on disconnect
                    reconnectTimeout = setTimeout(connectWebSocket, 5000);
                }
            });

            socket.addEventListener("error", () => {
                toast.error(t("chef.toast.error"), { id: "chef-ws" });
                try {
                    socket?.close();
                    getOrders(); // Refresh orders on error
                } catch {
                    // Ignore close errors
                }
            });
        };

        connectWebSocket();

        const handleVisibility = () => {
            if (
                document.visibilityState === "visible" &&
                (!socket || socket.readyState > 1) &&
                !manuallyClosed.current
            ) {
                toast(t("chef.toast.visible"), { id: "chef-ws" });
                connectWebSocket();
            }
        };

        const handleOnline = () => {
            if (!manuallyClosed.current && (!socket || socket.readyState > 1)) {
                toast(t("chef.toast.online"), { id: "chef-ws" });
                connectWebSocket();
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        window.addEventListener("online", handleOnline);

        return () => {
            manuallyClosed.current = true;

            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("online", handleOnline);

            toast.dismiss("chef-ws");

            if (heartbeatInterval) clearInterval(heartbeatInterval);
            if (reconnectTimeout) clearTimeout(reconnectTimeout);

            if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
                socket.close();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, base_socket]);



    const getOrders = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/orders?locale=${lang}`, {
                headers: {
                    Authorization: token,
                },
            });
            const orders: Order[] = response.data.orders;

            const newList = orders.filter(order => !order.progress); // null/undefined
            const preparingList = orders.filter(order => order.progress === "preparing");

            setNewOrders(newList);
            setPreparingOrders(preparingList);
            setFinishedOrders([]); // Always empty
        } catch (error) {
            console.error(error);
        }
    };


    const [shouldRefetch, setShouldRefetch] = useState(false);

    // 👀 1. Detect visibility or app resume
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setShouldRefetch(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        let removeAppListener: (() => void) | undefined;

        App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                setShouldRefetch(true);
            }
        }).then(listener => {
            removeAppListener = listener.remove;
        });

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (removeAppListener) removeAppListener();
        };
    }, []);

    // 🔁 2. Handle refetch when triggered
    useEffect(() => {
        if (!token) return;

        getOrders();
        setShouldRefetch(false); // Reset the flag after fetching
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefetch, token]);

    // Helper to get column by id
    const findColumnById = (id: string): ColumnId | undefined => {
        if (newOrders.find(order => order._id === id)) return "new";
        if (preparingOrders.find(order => order._id === id)) return "preparing";
        if (finishedOrders.find(order => order._id === id)) return "finished";
        return undefined;
    };

    // Helper to get orders array by columnId
    const getOrdersByColumn = (columnId: ColumnId) => {
        switch (columnId) {
            case "new":
                return newOrders;
            case "preparing":
                return preparingOrders;
            case "finished":
                return finishedOrders;
        }
    };

    // Helper to set orders array by columnId
    const setOrdersByColumn = (columnId: ColumnId, orders: Order[]) => {
        switch (columnId) {
            case "new":
                setNewOrders(orders);
                break;
            case "preparing":
                setPreparingOrders(orders);
                break;
            case "finished":
                setFinishedOrders(orders);
                break;
        }
    };

    function handleDragStart(event: DragStartEvent) {
        setActiveId(event.active.id as string);
    }


    interface UpdateOrderResponse {
        success: boolean;
        message?: string;
    }

    interface UpdateOrderParams {
        id: string;
        progress: string;
        type: string;
    }

    async function updateOrderProgress({ id, progress, type }: UpdateOrderParams): Promise<void> {
        return axios
            .put(`${baseUrl}/api/dashboard/orders/${id}/update`, { progress, type }, {
                headers: {
                    Authorization: token,
                },
            })
            .then((response) => {
                if (response.data.success) {
                    toast.success(t("ordersBody.OrderProgressUpdatedSuccessfully") as string);
                    if(progress === "finished" && isChef) openAppReviewBasedOnRole();
                } else {
                    getOrders(); // Refresh orders on error
                    throw new Error(response.data.message || t("ordersBody.OrderProgressUpdateFailed"));
                }
            })
            .catch((error) => {
                const axiosError = error as AxiosError<UpdateOrderResponse>;

                getOrders(); // 

                console.error("Update Order Progress Error:", axiosError);
                throw error;
            });
    }





    // Get the currently dragged order
    const activeOrder =
        activeId &&
        [...newOrders, ...preparingOrders, ...finishedOrders].find((o) => o._id === activeId);


    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        setActiveId(null);
        if (!over) return;

        const activeId = active.id as string;
        const overId = over.id as string;

        const activeContainer = findColumnById(activeId);
        const overContainer = ["new", "preparing", "finished"].includes(overId)
            ? (overId as ColumnId)
            : findColumnById(overId);

        if (!activeContainer || !overContainer) return;

        // ❌ Restrict invalid backward movement
        if (
            (activeContainer === "finished" && overContainer !== "finished") ||
            (activeContainer === "preparing" && overContainer === "new")
        ) {
            return;
        }

        const fromOrders = getOrdersByColumn(activeContainer);
        const toOrders = getOrdersByColumn(overContainer);
        const movingOrder = fromOrders.find((o) => o._id === activeId);
        if (!movingOrder) return;

        // Reordering in the same column
        if (activeContainer === overContainer) {
            const activeIndex = fromOrders.findIndex((o) => o._id === activeId);
            const overIndex = toOrders.findIndex((o) => o._id === overId);

            if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
                const updatedOrders = [...fromOrders];
                const [movedOrder] = updatedOrders.splice(activeIndex, 1);
                updatedOrders.splice(overIndex, 0, movedOrder);
                setOrdersByColumn(activeContainer, updatedOrders);
            }
            return;
        }

        // ✅ Optimistically update UI
        const updatedFromOrders = fromOrders.filter((o) => o._id !== activeId);
        const insertIndex = toOrders.findIndex((o) => o._id === overId);
        const updatedToOrders = [...toOrders];
        const insertAt = insertIndex >= 0 ? insertIndex : toOrders.length;
        updatedToOrders.splice(insertAt, 0, movingOrder);

        // Save old state to revert in case of failure
        const previousFromOrders = [...fromOrders];
        const previousToOrders = [...toOrders];

        // Update UI immediately
        setOrdersByColumn(activeContainer, updatedFromOrders);
        setOrdersByColumn(overContainer, updatedToOrders);

        // Attempt to update the backend
        updateOrderProgress({
            id: movingOrder._id,
            progress: overContainer,
            type: movingOrder.orderType,
        })
            .then(() => {

            })
            .catch(() => {
                // ❌ Revert UI
                setOrdersByColumn(activeContainer, previousFromOrders);
                setOrdersByColumn(overContainer, previousToOrders);

                toast.error(t("ordersBody.OrderProgressUpdateFailed") as string);
            });
    }



    return (
        <>
            <Link to={"/chefs-orders-history"} className="my-btn ms-auto block  mb-5 w-fit px-5 py-1">
                {t("chef.history")}
            </Link>
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-5 w-full  mt-6 shadow-sm">
                <p className="text-yellow-900 text-sm md:text-lg font-semibold mb-3 tracking-wide">
                    {t("chef.message")}
                </p>
                <p className="text-yellow-700 text-[12px] md:text-sm font-medium leading-relaxed italic">
                    {t("chef.warning")}
                </p>
            </div>


            <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="max-h-[95vh] min-w-full mb-5">
                    <div className="h-full w-full select-none overflow-x-auto">
                        <div className=" mx-auto w-full flex gap-1">
                            <DroppableColumnWrapper id="new">
                                <OrderColumn title="New Orders" orders={newOrders} columnId="new" />
                            </DroppableColumnWrapper>
                            <DroppableColumnWrapper id="preparing">
                                <OrderColumn title="Preparing Orders" orders={preparingOrders} columnId="preparing" />
                            </DroppableColumnWrapper>
                            <DroppableColumnWrapper id="finished">
                                <OrderColumn title="Finished Orders" orders={finishedOrders} columnId="finished" />
                            </DroppableColumnWrapper>
                        </div>
                    </div>
                </div>



                <DragOverlay>
                    {activeOrder ? (
                        <OrderCard
                            order={activeOrder}
                            isDragging
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </>
    );
}
